'use client';

import { useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { hasUrlOrTaskQuestion } from '@/shared-question-model';
import {
  isMobileUtMode,
  parsePanel,
  useProjectResponseModeContext,
} from '@/shared-survey';
import { isSurveyExpiredError, useInactivityTimeout } from '@/shared-utilities';
import type {
  GetProjectQuery,
  InitiateSurveyV3Mutation,
} from '@/survey-graphql';

import { useAudioQueueController } from './project-response-audio-controller.hooks';
import {
  useChannel,
  useResponderEmail,
} from './project-response-insight-info.hooks';
import { useProjectResponse } from './project-response.hooks';
import { toRecordingQuestionKind } from './project-usability-recording-question-kind.helpers';
import {
  useUsabilityProjectResponse,
  useUsabilityRecording,
} from './project-usability-recording.hooks';

interface UsabilityResponseSessionOptions {
  project: GetProjectQuery['project'];
  sessionId: string;
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
}

type ProjectResponse = ReturnType<typeof useProjectResponse>;
type UsabilityRecording = ReturnType<typeof useUsabilityRecording>;

export interface UsabilityResponseSession
  extends
    Pick<
      ProjectResponse,
      | 'isPublished'
      | 'isSaving'
      | 'isSubmitting'
      | 'messages'
      | 'insightLoading'
      | 'isConversationOver'
      | 'progress'
      | 'estimatedTotalTimeInSeconds'
      | 'handleConversationOver'
      | 'insightId'
      | 'conversationOverRef'
      | 'activityStore'
      | 'getSpeech'
      | 'speechAudios'
    >,
    Pick<
      ReturnType<typeof useUsabilityProjectResponse>,
      'contentUrl' | 'mobileAppEmbedUrl' | 'handleSubmitWithSidebar'
    >,
    Pick<
      UsabilityRecording,
      | 'isRecording'
      | 'isFinalizingRecording'
      | 'isUploadStalled'
      | 'retryUpload'
      | 'uploadProgress'
      | 'endRecording'
      | 'isPermissionDenied'
      | 'isRequestingPermission'
      | 'isPaused'
      | 'isScreenShareMuted'
    >,
    Pick<
      ReturnType<typeof useInactivityTimeout>,
      | 'isPausedByTimeout'
      | 'showTimeoutOverlay'
      | 'currentRemainingTime'
      | 'handleResumeRecording'
    > {
  searchParams: ReturnType<typeof useSearchParams>;
  isMobileMode: boolean;
  handleImageChoiceSubmit: (content: string) => Promise<void>;
  recordingError: UsabilityRecording['error'];
  startRecording: UsabilityRecording['start'];
  stopRecording: UsabilityRecording['stop'];
  effectiveIsConversationOver: boolean;
  showSubmitted: boolean;
  finalizationStarted: boolean;
  hasUrlOrTask: boolean;
}

export function useUsabilityResponseSession({
  project,
  sessionId,
  firstQuestion,
}: UsabilityResponseSessionOptions): UsabilityResponseSession {
  const responderEmail = useResponderEmail();
  const channel = useChannel();
  const searchParams = useSearchParams();
  const panel = parsePanel(searchParams);
  const isMobileMode = isMobileUtMode(searchParams);

  const [finalizationStarted, setFinalizationStarted] = useState(false);

  const { responseMode } = useProjectResponseModeContext();

  useAudioQueueController(project?.isAiVoiceEnabled ?? false, responseMode);

  const {
    isPublished,
    isSaving,
    isSubmitting,
    messages,
    handleSubmit,
    insightLoading,
    isConversationOver,
    progress,
    estimatedTotalTimeInSeconds,
    handleConversationOver,
    showSubmittedScreen,
    insightId,
    conversationOverRef,
    activityStore,
    getSpeech,
    speechAudios,
  } = useProjectResponse({
    project,
    responderEmail,
    channel,
    sessionId,
    firstQuestion,
    panel,
    enableSubmittedScreen: true,
  });

  const { contentUrl, mobileAppEmbedUrl, handleSubmitWithSidebar } =
    useUsabilityProjectResponse({
      firstQuestion,
      handleSubmit: async (values) => {
        updateActivity();
        return handleSubmit(values);
      },
    });

  const handleImageChoiceSubmit = async (content: string) => {
    try {
      await handleSubmitWithSidebar({ content });
    } catch (error) {
      if (!isSurveyExpiredError(error)) throw error;
    }
  };

  const {
    isRecording,
    isFinalizingRecording,
    isUploadStalled,
    retryUpload,
    uploadProgress,
    error: recordingError,
    start: startRecording,
    stop: stopRecording,
    endRecording,
    isPermissionDenied,
    isRequestingPermission,
    updateActivity,
    getRemainingTime,
    isTimedOut,
    pauseRecording,
    resumeRecording,
    isPaused,
    isScreenShareMuted,
  } = useUsabilityRecording({
    insightId,
    isAiVoiceEnabled: project?.isAiVoiceEnabled ?? false,
    skipLocalMic: isMobileMode,
  });

  const effectiveIsConversationOver =
    isConversationOver && !isFinalizingRecording;

  const showSubmitted = showSubmittedScreen && !isFinalizingRecording;

  const {
    isPausedByTimeout,
    showTimeoutOverlay,
    currentRemainingTime,
    handleResumeRecording,
  } = useInactivityTimeout({
    isRecording,
    isPaused,
    getRemainingTime,
    isTimedOut,
    pauseRecording,
    resumeRecording,
    updateActivity,
  });

  if (isFinalizingRecording && !finalizationStarted) {
    setFinalizationStarted(true);
  }

  const hasUrlOrTask = hasUrlOrTaskQuestion(
    project.questions.map((question) => ({
      kind: toRecordingQuestionKind(question.type),
      contentUrl: question.contentUrl,
      text: question.text,
    }))
  );

  return {
    searchParams,
    isMobileMode,
    isPublished,
    isSaving,
    isSubmitting,
    messages,
    insightLoading,
    isConversationOver,
    progress,
    estimatedTotalTimeInSeconds,
    handleConversationOver,
    insightId,
    conversationOverRef,
    activityStore,
    getSpeech,
    speechAudios,
    contentUrl,
    mobileAppEmbedUrl,
    handleSubmitWithSidebar,
    handleImageChoiceSubmit,
    isRecording,
    isFinalizingRecording,
    isUploadStalled,
    retryUpload,
    uploadProgress,
    recordingError,
    startRecording,
    stopRecording,
    endRecording,
    isPermissionDenied,
    isRequestingPermission,
    isPaused,
    isScreenShareMuted,
    effectiveIsConversationOver,
    showSubmitted,
    isPausedByTimeout,
    showTimeoutOverlay,
    currentRemainingTime,
    handleResumeRecording,
    finalizationStarted,
    hasUrlOrTask,
  };
}
